import React, {ChangeEventHandler, FocusEventHandler, useCallback, useEffect, useState} from 'react'
import {CFValue} from 'module/CustomField/type/CFType'
import {CFComponentProps} from 'module/CustomField/type/CFComponentType'
import {stripHTML} from 'util/StringUtil'
import {css} from '@emotion/react'
import {getCFAttrTextAlignCSS} from 'module/CustomField/style/CFAlignmentStyle'

interface CustomFieldTextareaComponentProps extends CFComponentProps<CFValue> {
	textareaRows?: number
}

export function CustomFieldTextareaComponent({modelValue, disabled, setCb, textareaRows, placeholderHTML, cfDef}: CustomFieldTextareaComponentProps) {
	const [value, setValue] = useState(modelValue)
	const cssWrapper = css`
		// move border outside into a wrapper div to prevent webkit engine from blurring text
		border: 1px solid hsl(0, 0%, 80%);
		box-sizing: border-box;
		border-radius: 4px;
		overflow: hidden;
		min-height: ${textareaRows && '1.5em' || 90};
		max-height: ${(textareaRows && textareaRows == 1) && '1.5em' || 'unset'};

		&:hover {
			border-color: hsl(0, 0%, 70%);
		}

		&:focus-within {
			border-color: #2684FF;
			box-shadow: 0 0 0 1px #2684FF;
		}
	`
	const cssInput = css`
		height: 100%;
		width: 100%;
		box-sizing: border-box;
		background: ${disabled ? 'unset' : 'white'};
		min-height: ${textareaRows ? '1.5em' : '90px'};
		max-height: ${(textareaRows && textareaRows == 1) ? '1.5em' : 'unset'};
		padding: 3px 6px;
		outline: none;
		border: none;
		display: block;
		resize: none;
		font: inherit;
		font-size: 1em;
	`

	useEffect(() => {
		setValue(modelValue)
	}, [modelValue])
	const onBlur = useCallback<FocusEventHandler<HTMLTextAreaElement>>(({target}) => {
		if (!disabled) setCb(target.value)
		setValue(modelValue)
	}, [setCb, disabled, modelValue])
	const onChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>((evt) => {
		setValue(evt.target.value)
	}, [])
	const onFocus: FocusEventHandler<HTMLTextAreaElement> = useCallback((evt) => {
		const {target} = evt;
		setTimeout(() => {
			if (document.activeElement === target) target.select()
		}, 60);
	}, [])

	return (
		<div
			css={cssWrapper}
			data-cursor-key-needs-modifier={true}
		>
			<textarea
				placeholder={stripHTML(placeholderHTML)}
				css={[
					cssInput,
					getCFAttrTextAlignCSS(cfDef),
				]}
				value={value || ''}
				disabled={disabled}
				onChange={onChange}
				onBlur={onBlur}
				rows={textareaRows}
				onFocus={onFocus}
			/>
		</div>
	)
}